<template>
  <ScaleLoader v-if="loading" />
  <div v-else>
    <ExportButtons
      :csv="exportGridsToExcel"
      :pdf="exportGridsToPdf"
      :xlsx="exportGridsToExcel"
    />
    <div class="vehicle-report-grid-card">
      <p class="vehicle-report-grid-title">
        {{ $t('components.reports.statistics.statistics') }}
      </p>
      <DxDataGrid
        :ref="dataGridRef"
        :allow-column-reordering="true"
        :allow-column-resizing="true"
        :column-auto-width="true"
        :column-hiding-enabled="true"
        class="vehicle-report-grid"
        :data-source="store"
        :hover-state-enabled="true"
        :remote-operations="remoteOperations"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="false"
      >
        <DxHeaderFilter :visible="false" />
        <DxPaging
          :page-index="0"
          :page-size="25"
        /> <!-- Shows the second page -->
        <DxPager
          :allowed-page-sizes="[10, 20, 25]"
          :show-info="true"
          :show-navigation-buttons="true"
          :show-page-size-selector="true"
        />
        <DxFilterRow :visible="true" />
        <DxColumnFixing :enabled="true" />
        <DxColumn
          :allow-filtering="true"
          :allow-header-filtering="false"
          :caption="$t('components.reports.statistics.registrationOrName')"
          :filter-operations="allowedOperations"
          data-field="registration_or_name"
        />
        <DxColumn
          :allow-filtering="true"
          :allow-header-filtering="false"
          :caption="$t('components.reports.statistics.distance')"
          :filter-operations="allowedOperations"
          data-field="distance"
        />
        <DxColumn
          :allow-filtering="true"
          :allow-header-filtering="false"
          :caption="$t('components.reports.statistics.ignition')"
          :filter-operations="allowedOperations"
          :customize-text="formatMinutesCell"
          data-field="ignition"
        />
        <DxColumn
          :caption="$t('components.reports.statistics.details')"
          type="buttons"
        >
          <DxButton
            :hint="$t('components.reports.statistics.details')"
            icon="detailslayout"
            @click="displayDetails"
          />
        </DxColumn>
      </DxDataGrid>
      <!-- Modal with details -->
      <div
        id="detailsModal"
        aria-hidden="true"
        class="modal fade"
        tabindex="-1"
      >
        <div class="modal-dialog modal-fullscreen">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">
                {{ $t('components.reports.statistics.details') }}
              </h5>
              <button
                aria-label="Close"
                class="btn-close"
                data-bs-dismiss="modal"
                type="button"
              />
            </div>
            <div class="modal-body">
              <ExportButtons
                :csv="exportGridsToExcel"
                :details="true"
                :pdf="exportGridsToPdf"
                :xlsx="exportGridsToExcel"
              />
              <DxDataGrid
                :ref="dataGridRefDetails"
                :allow-column-reordering="true"
                :allow-column-resizing="true"
                :column-auto-width="true"
                :data-source="detailsStore"
                :hover-state-enabled="true"
                :remote-operations="false"
                :row-alternation-enabled="true"
                :show-borders="true"
                :show-column-lines="true"
                :show-row-lines="false"
              >
                <DxHeaderFilter :visible="false" />
                <DxPaging
                  :page-index="0"
                  :page-size="25"
                /> <!-- Shows the second page -->
                <DxPager
                  :allowed-page-sizes="[10, 20, 25]"
                  :show-info="true"
                  :show-navigation-buttons="true"
                  :show-page-size-selector="true"
                />
                <DxFilterRow :visible="true" />
                <DxColumnFixing :enabled="true" />
                <DxColumn
                  :allow-filtering="true"
                  :allow-header-filtering="false"
                  :caption="$t('components.reports.statistics.date')"
                  :filter-operations="allowedOperations"
                  :customize-text="formatDateCell"
                  data-field="date_from"
                />
                <DxColumn
                  :allow-filtering="true"
                  :allow-header-filtering="false"
                  :caption="$t('components.reports.statistics.distance')"
                  :filter-operations="allowedOperations"
                  data-field="distance"
                />
                <DxColumn
                  :allow-filtering="true"
                  :allow-header-filtering="false"
                  :caption="$t('components.reports.statistics.ignition')"
                  :filter-operations="allowedOperations"
                  :customize-text="formatMinutesCell"
                  data-field="ignition"
                />
              </DxDataGrid>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { getStatistics } from '@/crud/getters'
import {
  DxButton,
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging
} from 'devextreme-vue/data-grid'
import { Workbook } from 'exceljs'
import { saveAs } from 'file-saver'
import { exportDataGrid } from 'devextreme/excel_exporter'
import ExportButtons from '@/components/content/_partials/ExportButtons.vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter'
import { importFont } from '@/utils/jspdf/importFont'
import { getHumanDate, minutesToHoursMinutes } from '@/utils/tools'
import { Modal } from 'bootstrap'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import CustomStore from 'devextreme/data/custom_store'
import { buildGridLoadParams, normalizeGridResponse } from '@/services/reports/grid-store'

export default defineComponent({
  name: 'Statistics',
  components: {
    ExportButtons,
    DxColumn,
    DxColumnFixing,
    DxDataGrid,
    DxFilterRow,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    DxButton,
    ScaleLoader
  },
  props: {
    dateFrom: String,
    dateTo: String
  },
  data(): {
    store: any,
    detailsStore: Array<any>,
    allowedOperations: Array<string>,
    remoteOperations: boolean,
    dataGridRef: string,
    dataGridRefDetails: string,
    loading: boolean
  } {
    return {
      store: [],
      detailsStore: [],
      allowedOperations: ['contains', '=', 'notcontains', '!=', '>', '>=', '<', '<='],
      remoteOperations: true,
      dataGridRef: 'statisticsGrid',
      dataGridRefDetails: 'statisticsDetailsGrid',
      loading: true
    }
  },
  computed: {
    statisticsGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRef].instance
    },
    statisticsDetailsGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRefDetails].instance
    }
  },
  methods: {
    exportGridsToExcel(extension: string, details: boolean) {
      const self: any = this

      const workbook = new Workbook()
      const sheet = workbook
          .addWorksheet(self.$t('components.reports.tabs.statistics'))

      if (!details) {
        exportDataGrid({
          worksheet: sheet,
          component: self.statisticsGridInstance
        })
            .then(() => {
              if (extension == 'csv')
                workbook.csv.writeBuffer().then((buffer) => {
                  saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                      'Statistics.csv')
                })
              else if (extension == 'xlsx')
                workbook.xlsx.writeBuffer().then((buffer) => {
                  saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                      'Statistics.xlsx')
                })
            })
      } else {
        exportDataGrid({
          worksheet: sheet,
          component: self.statisticsDetailsGridInstance
        })
            .then(() => {
              if (extension == 'csv')
                workbook.csv.writeBuffer().then((buffer) => {
                  saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                      'Statistics_details.csv')
                })
              else if (extension == 'xlsx')
                workbook.xlsx.writeBuffer().then((buffer) => {
                  saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                      'Statistics_details.xlsx')
                })
            })
      }
    },
    exportGridsToPdf(details: boolean) {
      const doc = new jsPDF()
      importFont(doc)

      if (!details) {
        exportDataGridToPdf({
          jsPDFDocument: doc,
          component: this.statisticsGridInstance,
          // autoTableOptions: {
          //   styles: {
          //     font: 'Roboto'
          //   }
          // }
        }).then(() => {
          doc.text(this.$t('components.reports.tabs.statistics'), 25, 5, { baseline: 'top', align: 'center' })
          doc.save('Statistics.pdf')
        })
      } else {
        exportDataGridToPdf({
          jsPDFDocument: doc,
          component: this.statisticsDetailsGridInstance,
          // autoTableOptions: {
          //   styles: {
          //     font: 'Roboto'
          //   }
          // }
        }).then(() => {
          doc.text(this.$t('components.reports.statistics.details'), 25, 5, { baseline: 'top', align: 'center' })
          doc.save('Statistics_details.pdf')
        })
      }
    },
    displayDetails(e) {
      this.detailsStore = JSON.parse(JSON.stringify(e.row.data.by_days || []))

      const element: any = document.getElementById('detailsModal')
      const modal: any = new Modal(element)
      modal.show()
    },
    formatDateCell(cellInfo: any): string {
      return cellInfo.value ? getHumanDate(cellInfo.value) : ''
    },
    formatMinutesCell(cellInfo: any): string {
      return minutesToHoursMinutes(cellInfo.value || 0)
    }
  },
  mounted() {
    if (this.dateFrom && this.dateTo) {
      this.store = new CustomStore({
        load: (loadOptions: any) => getStatistics(
            this.dateFrom as string,
            this.dateTo as string,
            buildGridLoadParams(loadOptions)
        ).then((res) => normalizeGridResponse(res.data)).catch(() => {
          ;(this as any).$swal.fire({
            icon: 'warning',
            title: 'Oops...',
            text: this.$t('components.error')
          })
          return {data: [], totalCount: 0}
        })
      })
      this.loading = false
    }
  }
})
</script>
<style lang="scss" scoped src="@/assets/scss/report-grid.scss"></style>
